import { InterviewQuestion, PostJobStep } from '../../../../../store/workspace/types';
import { DayValue } from '@amir04lm26/react-modern-calendar-date-picker';
import { fromDatePickerFormat } from '../../../../../utils/dateHelpers';
import { EditIcon, WarningIcon } from '../../../../../assets/icons';
import MotionWrapper from '../../../../../layouts/MotionWrapper';
import Button from '../../../../../components/Button';
import Chips from '../../../../../components/Chips';
import styles from './style.module.scss';
import {
    City,
    Country,
    ListItem,
    CountryState,
} from '../../../../../store/general/types';

interface Props {
    city: City;
    posting: boolean;
    country: Country;
    jobTitle: string;
    deadline: DayValue;
    submit: () => void;
    state: CountryState;
    codingExam: boolean;
    salaryAmount: string;
    skipCodingExam: boolean;
    resumeMatching: boolean;
    jobDescription: string;
    jobType: ListItem | null;
    multipleChoiceExam: boolean;
    recruitingInterview: boolean;
    workplaceType: ListItem | null;
    codingExamLanguages: ListItem[];
    salaryFrequency: ListItem | null;
    multipleChoiceCodingExam: boolean;
    multipleChoiceExamSkills: string[];
    setStep: (step: PostJobStep) => void;
    selectedQuestions: InterviewQuestion[];
    multipleChoiceCodingExamLanguages: string[];
};

function Review(props: Props) {
    const {
        setStep,
        submit,
        city,
        state,
        posting,
        country,
        jobTitle,
        jobType,
        deadline,
        codingExam,
        salaryAmount,
        skipCodingExam,
        workplaceType,
        jobDescription,
        resumeMatching,
        salaryFrequency,
        selectedQuestions,
        multipleChoiceExam,
        recruitingInterview,
        codingExamLanguages,
        multipleChoiceExamSkills,
        multipleChoiceCodingExam,
        multipleChoiceCodingExamLanguages,
    } = props;

    const edit = (step: PostJobStep) => {
        if (posting) return;
        setStep(step);
    };

    return (
        <MotionWrapper className={styles.wrapper}>
            <span className={styles.title}>Post new job - Review</span>
            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Job information</span>
                    <EditIcon onClick={() => edit('JobDetail')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Job title</span>
                        <span>{jobTitle}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Job type</span>
                        <span>{jobType?.title}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Workplace type</span>
                        <span>{workplaceType?.title}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Base salary amount</span>
                        <span>{salaryAmount}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Base salary frequency</span>
                        <span>{salaryFrequency?.title}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>City</span>
                        <span>{city.city}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>State</span>
                        <span>{state.state}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Country</span>
                        <span>{country.title}</span>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Recruiting interview</span>
                    <EditIcon onClick={() => edit('RecruitingInterview')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Generate recruiting interview</span>
                        <span>
                            {recruitingInterview ? 'Yes' : 'No'}
                        </span>
                    </div>
                    <div className={styles.info_summary_item} data-wrap-in-mobile="true">
                        <span>Recruiting interview questions</span>
                        <div className={styles.questionList}>
                            {selectedQuestions.map((item, indx) => <span><b>{indx + 1}-</b> {item.title}</span>)}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Multiple choice exam</span>
                    <EditIcon onClick={() => edit('MultipleChoiceExamDetail')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Generate multiple choice exam</span>
                        <span>
                            {multipleChoiceExam ? 'Yes' : 'No'}
                        </span>
                    </div>
                    {!skipCodingExam && <div className={styles.info_summary_item}>
                        <span>Have coding questions</span>
                        <span>
                            {multipleChoiceCodingExam ? 'Yes' : 'No'}
                        </span>
                    </div>}
                    {!skipCodingExam && <div className={styles.info_summary_item}>
                        <span>Languages for coding questions</span>
                        <div className={styles.arrayList}>
                            {multipleChoiceCodingExamLanguages
                                .filter(i => Boolean(i.trim()))
                                .map(i => <Chips title={i} />)}
                        </div>
                    </div>}
                    <div className={styles.info_summary_item}>
                        <span>Skills to cover in greater details:</span>
                        <div className={styles.arrayList}>
                            {multipleChoiceExamSkills
                                .filter(i => Boolean(i.trim()))
                                .map(i => <Chips title={i} />)}
                        </div>
                    </div>
                </div>
            </div>
            {!skipCodingExam && <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Coding exam</span>
                    <EditIcon onClick={() => edit('CodingExamDetail')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Generate coding exam</span>
                        <span>{codingExam ? 'Yes' : 'No'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Programming languages</span>
                        <div className={styles.arrayList}>
                            {codingExamLanguages.map(i => <Chips title={i.title} />)}
                        </div>
                    </div>
                </div>
            </div>}
            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Exam deadline and job description</span>
                    <EditIcon onClick={() => edit('ExamDeadline')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>The job matches candidates in our database</span>
                        <span>{resumeMatching ? 'Yes' : 'No'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Exam deadline</span>
                        <span>{deadline ? fromDatePickerFormat(deadline) : '----'}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <p>
                            Job description:
                            <br /><br />
                            {jobDescription}
                        </p>
                    </div>
                </div>
            </div>
            {!posting && <div className={styles.actions}>
                {(!recruitingInterview && !multipleChoiceExam && !codingExam) &&
                    <div className={styles.waiting} style={{ marginRight: 'auto', color: '#FF0000' }}>
                        <WarningIcon />
                        <span>
                            Activate at least one exam type to send the job post.
                        </span>
                    </div>}
                <Button
                    text='Back'
                    type='Outline-Primary'
                    className={styles.actions_btn}
                    onClick={() => setStep('ExamDeadline')}
                />
                <Button
                    type='Primary'
                    text='Save Job Post'
                    onClick={submit}
                    loading={posting}
                    className={styles.actions_btn}
                    disabled={!recruitingInterview && !multipleChoiceExam && !codingExam}
                />
            </div>}
        </MotionWrapper>
    )
};

export default Review;
